<template>
    <v-dialog v-model="modal" width="1200">
        <v-card :loading="isLoading">
            <v-card-title>
                <v-icon class="v-card-close mt-2 me-2" @click="modal = false">mdi-close</v-icon>
                <v-btn
                    v-if="paymentMethods && paymentMethods.length"
                    class="me-3"
                    size="small"
                    elevation="0"
                    @click="paymentMethods = []"
                >
                    <v-icon> mdi-arrow-left</v-icon>
                </v-btn>
                <span v-if="!$conf.public.account.paymentSchedulerAutoPaymentMode">
                    {{ `${$t('payment_schedule')}` }}
                </span>
                <span v-else>
                    {{ `${$t('payments')}` }}
                </span>
            </v-card-title>
            <v-card-text>
                <template v-if="!paymentMethods.length">
                    <v-row v-if="isShowTypeSwitcher" class="my-2">
                        <v-col cols="12" md="6">
                            <v-select
                                v-model="invoiceType"
                                :items="invoiceTypes"
                                hide-details
                                variant="solo"
                                @update:model-value="loadManualInvoices"
                            />
                        </v-col>
                        <v-col v-if="isTO1" cols="12" md="6">
                            <div class="full-width d-flex align-center">
                                <v-spacer />
                                <v-checkbox v-model="isShowCancelledPayments" :label="$t('show_cancelled_payments')" />
                            </div>
                        </v-col>
                    </v-row>
                    <v-row v-if="!manualInvoicesFiltered.length">
                        <v-col cols="12" class="d-flex align-center flex-column justify-center">
                            <div class="text-center text-caption text--secondary">
                                {{ isLoading ? `${$t('loading')}...` : $t('no_data') }}
                            </div>
                        </v-col>
                    </v-row>
                    <v-data-table v-else :items="manualInvoicesFiltered" :headers="headers" disable-sort>
                        <template v-slot:item.dueDate="{item}">
                            <div class="d-flex align-center">
                                <template v-if="item !== editDueDateItem">
                                    <div v-if="item.dueDate" class="text-no-wrap">
                                        {{ $filters.dateShortFormat(item.dueDate) }}
                                    </div>
                                    <v-btn
                                        v-if="item.dueDate && isTO1"
                                        size="small"
                                        icon="mdi-pencil"
                                        variant="text"
                                        color="primary"
                                        class="ms-1"
                                        @click="editDueDate(item)"
                                    />
                                </template>
                                <template v-else>
                                    <date-picker
                                        v-if="item === editDueDateItem"
                                        v-model="newDueDate"
                                        outlined
                                        dense
                                        hide-details
                                        width="180"
                                    />
                                    <v-btn
                                        :disabled="!newDueDate"
                                        size="small"
                                        icon="mdi-check-circle-outline"
                                        variant="text"
                                        color="success"
                                        class="ms-1"
                                        @click="saveNewDueDate"
                                    />
                                </template>
                            </div>
                        </template>
                        <template v-slot:item.paymentStatus="{item}">
                            <div class="d-flex flex-column">
                                <div class="d-flex align-center">
                                    <span :class="getStatusColor(item)">
                                        {{ $t(`paymentStatus.${item.paymentStatus}`) }}
                                    </span>
                                    <v-tooltip v-if="isShowInfoButton(item)" location="top">
                                        <template v-slot:activator="{props}">
                                            <v-btn
                                                icon="mdi-information-outline"
                                                variant="text"
                                                size="small"
                                                v-bind="item.paymentInfos.find(i => !!i.remark) ? props : null"
                                                @click="
                                                    showedBankTransferInvoiceId === item.invoiceId
                                                        ? (showedBankTransferInvoiceId = null)
                                                        : (showedBankTransferInvoiceId = item.invoiceId)
                                                "
                                            />
                                        </template>
                                        <div class="d-flex flex-column">
                                            <span v-for="info in item.paymentInfos" :key="info.remark">{{
                                                info.remark
                                            }}</span>
                                        </div>
                                    </v-tooltip>
                                </div>
                                <v-expand-transition>
                                    <div
                                        v-if="showedBankTransferInvoiceId === item.invoiceId"
                                        class="d-flex flex-column"
                                    >
                                        <span v-if="item.description" class="text-caption">
                                            {{ `${$t('description')}: ${item.description}` }}
                                        </span>
                                        <div v-for="(info, i) in item.paymentInfos" :key="i" class="d-flex flex-column">
                                            <div
                                                v-if="Object.keys({...info.bankTransferDetails}).length"
                                                class="d-flex flex-column"
                                            >
                                                <div>
                                                    <span>
                                                        {{ $t('bank_transfer.account') + ': ' }}
                                                    </span>
                                                    <span>
                                                        {{ info.bankTransferDetails.account }}
                                                    </span>
                                                </div>
                                                <div>
                                                    <span>
                                                        {{ $t('bank_transfer.bank') + ': ' }}
                                                    </span>
                                                    <span>
                                                        {{ info.bankTransferDetails.bank }}
                                                    </span>
                                                </div>
                                                <div>
                                                    <span>
                                                        {{ $t('bank_transfer.branch') + ': ' }}
                                                    </span>
                                                    <span>
                                                        {{ info.bankTransferDetails.branch }}
                                                    </span>
                                                </div>
                                                <div>
                                                    <span>
                                                        {{ $t('bank_transfer.iban') + ': ' }}
                                                    </span>
                                                    <span>
                                                        {{ info.bankTransferDetails.iban }}
                                                    </span>
                                                </div>
                                                <div>
                                                    <span>
                                                        {{ $t('bank_transfer.receiving_bank') + ': ' }}
                                                    </span>
                                                    <span>
                                                        {{ info.bankTransferDetails.receivingBank }}
                                                    </span>
                                                </div>
                                                <div>
                                                    <span>
                                                        {{ $t('bank_transfer.reference') + ': ' }}
                                                    </span>
                                                    <span>
                                                        {{ info.bankTransferDetails.reference }}
                                                    </span>
                                                </div>
                                                <div>
                                                    <span>
                                                        {{ $t('bank_transfer.swift_code') + ': ' }}
                                                    </span>
                                                    <span>
                                                        {{ info.bankTransferDetails.swiftCode }}
                                                    </span>
                                                </div>
                                                <div>
                                                    <span>
                                                        {{ $t('bank_transfer.value_date') + ': ' }}
                                                    </span>
                                                    <span v-if="info.bankTransferDetails.valueDate">
                                                        {{
                                                            $filters.dateShortFormat(info.bankTransferDetails.valueDate)
                                                        }}
                                                    </span>
                                                </div>
                                            </div>
                                            <div v-if="info.remark">
                                                <span>
                                                    {{ $t('remark') + ': ' }}
                                                </span>
                                                <span>
                                                    {{ info.remark }}
                                                </span>
                                            </div>
                                            <div
                                                v-if="
                                                    info.exchangeRatesMap && Object.values(info.exchangeRatesMap).length
                                                "
                                            >
                                                <span>
                                                    {{ $t('exchange_rate') + ': ' }}
                                                </span>
                                                <span>
                                                    {{ Object.values(info.exchangeRatesMap)[0] }}
                                                </span>
                                            </div>
                                            <v-divider v-if="i !== item.paymentInfos.length - 1" class="my-1" />
                                        </div>
                                    </div>
                                </v-expand-transition>
                            </div>
                        </template>
                        <template v-slot:item.payDate="{item}">
                            <div class="d-flex align-center">
                                <template v-if="item !== editPayDateItem">
                                    <div v-if="item.payDate" class="text-no-wrap">
                                        {{ $filters.dateShortFormat(item.payDate) }}
                                    </div>
                                    <v-btn
                                        v-if="item.payDate && isTO1"
                                        size="small"
                                        icon="mdi-pencil"
                                        variant="text"
                                        color="primary"
                                        class="ms-1"
                                        @click="editPayDate(item)"
                                    />
                                </template>
                                <template v-else>
                                    <date-picker
                                        v-if="item === editPayDateItem"
                                        v-model="newPayDate"
                                        outlined
                                        :min="minPayDate"
                                        dense
                                        hide-details
                                        width="180"
                                    />
                                    <v-btn
                                        :disabled="!newPayDate"
                                        :loading="newPayDateSaveLoading"
                                        size="small"
                                        icon="mdi-check-circle-outline"
                                        variant="text"
                                        color="success"
                                        class="ms-1"
                                        @click="saveNewPayDate"
                                    />
                                </template>
                            </div>
                        </template>
                        <template v-slot:item.amountToPay="{item}">
                            <span v-if="item.originalPrices && item.originalPrices[0]" class="text-no-wrap">
                                {{
                                    $filters.formatPrice({
                                        amount: item.originalPrices[0].originalAmount,
                                        currency: item.originalPrices[0].originalAmountCurrency,
                                    })
                                }}
                            </span>
                        </template>
                        <template v-slot:item.pay="{item}">
                            <v-btn
                                v-if="item.paymentStatus === 'BILL'"
                                color="primary"
                                size="small"
                                elevation="0"
                                :disabled="isLoading"
                                @click="initPay(item)"
                            >
                                <v-icon start size="small"> mdi-plus-circle-multiple-outline </v-icon>
                                <span class="text-caption">
                                    {{ $t('pay') }}
                                </span>
                            </v-btn>
                        </template>
                        <template v-slot:item.cancel="{item}">
                            <v-btn
                                v-if="isShowCancelButton(item)"
                                size="small"
                                elevation="0"
                                :disabled="isLoading"
                                color="error"
                                @click="isShowCancelModalItem = item"
                            >
                                <span class="text-caption">
                                    {{ $t('cancel') }}
                                </span>
                            </v-btn>
                        </template>
                    </v-data-table>
                </template>
                <order-manual-invoice-payment-form
                    v-else
                    :payment-methods="paymentMethods"
                    :order-id="orderId"
                    :invoice-type="invoiceType"
                    :client-price-currencies="clientPriceCurrencies"
                    @complete-payment="completePayment"
                    @back="paymentMethods = []"
                />
            </v-card-text>
        </v-card>
        <confirmation-modal
            :model-value="!!isShowCancelModalItem"
            :text="$t('invoice_cancel_confirmation_label')"
            :confirmation-btn-text="$t('confirm')"
            :close-btn-text="$t('cancel')"
            @close="isShowCancelModalItem = null"
            @confirm="cancelInvoice(isShowCancelModalItem)"
        />
    </v-dialog>
</template>

<script lang="ts">
    import {Component, Emit, Prop, VModel, Vue, Watch} from 'vue-facing-decorator'
    import OrderManualInvoicePaymentForm from '@/components/account/OrderManualInvoicePaymentForm.vue'
    import DatePicker from '@/components/snippets/forms/DatePicker.vue'
    import ConfirmationModal from '~/components/modals/ConfirmationModal.vue'

    @Component({
        components: {ConfirmationModal, OrderManualInvoicePaymentForm, DatePicker},
    })
    export class OrderPaymentSchedule extends Vue {
        @VModel() modal
        @Prop() objectId
        @Prop() orderId
        @Prop() cancelInvoicesOnLoad
        @Prop() clientPriceCurrencies
        @Prop() _isManualInvoicesLoading

        paymentOptionsLoading = false
        newDueDateSaveLoading = false
        newPayDateSaveLoading = false
        cancelInvoiceLoading = false

        paymentMethods = []
        manualInvoices = []
        issueInfoItems = []

        newDueDate = null
        newPayDate = null
        editDueDateItem = null
        editPayDateItem = null
        showedBankTransferInvoiceId = null

        invoiceType = 'CUSTOMER'

        silentPay = false
        isShowCancelledPayments = false
        isShowCancelModalItem = null

        async initSilentPay(invoiceId) {
            this.reset()
            this.silentPay = true
            await this.$nextTick(() => {})
            await this.initPay({invoiceId})
            this.modal = true
        }

        @Emit()
        changeIsManualInvoicesLoading(val) {
            return val
        }

        @Emit()
        completePayment() {}

        @Watch('modal')
        async onModalChange() {
            if (this.modal) {
                if (!this.silentPay) {
                    this.reset()
                    await this.loadManualInvoices()
                }
            } else {
                if (this.cancelInvoicesOnLoad) {
                    await this.cancelInvoiceList(
                        this.manualInvoices.filter(invoice => invoice.paymentStatus === 'BILL')
                    )
                }
                this.silentPay = false
            }
        }

        @Emit()
        async loadManualInvoices() {
            this.manualInvoices = []
            this.nextManualInvoice = null

            this.isManualInvoicesLoading = true

            try {
                let manualInvoices = (
                    await this.$api.manualInvoices.get({
                        orderId: this.objectId,
                        invoiceType: this.invoiceType,
                        limit: -1,
                    })
                ).invoices
                    .sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate))
                    .sort(a => (a.paymentStatus === 'CANCELED' ? 1 : -1))
                    .sort(a => (a.paymentStatus === 'BILL' ? -1 : 1))

                if (this.cancelInvoicesOnLoad && !this.silentPay) {
                    await this.cancelInvoiceList(manualInvoices.filter(invoice => invoice.paymentStatus === 'BILL'))
                }

                manualInvoices = manualInvoices.filter(el =>
                    this.$conf.public.account.paymentSchedulerAutoPaymentMode ? el.paymentStatus !== 'CANCELED' : true
                )

                if (this.modal) {
                    this.issueInfoItems = (
                        await this.$api.manualInvoiceIssueInfo.get({
                            orderId: this.objectId,
                            invoiceType: this.invoiceType,
                        })
                    ).items

                    await this.$nextTick(() => {})

                    this.issueInfoItems.forEach(item => {
                        item.prices.forEach(price => {
                            manualInvoices.push({
                                amountToPay: price.amount,
                                amountToPayCurrency: price.currency,
                                originalPrices: [
                                    {
                                        originalAmount: price.amount,
                                        originalAmountCurrency: price.currency,
                                    },
                                ],
                                paymentStatus: 'NO_BILL',
                                supplierName: item.supplierName,
                            })
                        })
                    })
                }

                this.manualInvoices = manualInvoices

                this.nextManualInvoice = this.manualInvoices?.find(m => m.paymentStatus === 'BILL')
                // eslint-disable-next-line no-empty
            } catch (e) {
            } finally {
                this.isManualInvoicesLoading = false
            }

            await this.$nextTick(() => {})

            return this.manualInvoices
        }

        async getPaymentOptions(invoiceId) {
            this.paymentOptionsLoading = true

            try {
                return (
                    await this.$api.manualInvoicePaymentMethods.get({
                        orderId: this.objectId,
                        invoiceType: this.invoiceType,
                        invoiceId: invoiceId,
                    })
                ).invoicePaymentMethods?.sort((a, b) => {
                    const nameA = a.paymentMethod || this.$t('bank_transfer.bank_transfer')
                    const nameB = b.paymentMethod || this.$t('bank_transfer.bank_transfer')

                    return nameA?.localeCompare(nameB)
                })

                // eslint-disable-next-line no-empty
            } catch (e) {
            } finally {
                this.paymentOptionsLoading = false
            }
        }

        async initPay(item) {
            this.paymentMethods = await this.getPaymentOptions(item.invoiceId)

            return this.paymentMethods
        }

        async saveNewDueDate() {
            this.newDueDateSaveLoading = true

            try {
                await this.$api.manualInvoices.put(this.editDueDateItem.invoiceId, {
                    dueToPay: `${this.newDueDate}T23:59`,
                })

                this.editDueDateItem.dueDate = `${this.newDueDate} 23:59`
                this.editDueDateItem = null

                // eslint-disable-next-line no-empty
            } catch (e) {
            } finally {
                this.newDueDateSaveLoading = false
            }
        }

        async saveNewPayDate() {
            this.newPayDateSaveLoading = true

            try {
                await this.$api.manualInvoices.put(this.editPayDateItem.invoiceId, {
                    transactionDateTime: `${this.newPayDate}T23:59`,
                })

                this.editPayDateItem.payDate = `${this.newPayDate} 23:59`
                this.editPayDateItem = null

                // eslint-disable-next-line no-empty
            } catch (e) {
            } finally {
                this.newPayDateSaveLoading = false
            }
        }

        async cancelInvoiceList(invoices) {
            await Promise.all(
                invoices.map(async invoice => {
                    return await this.cancelInvoice(invoice)
                })
            )
        }

        async cancelInvoice(item) {
            this.isShowCancelModalItem = null
            this.cancelInvoiceLoading = true

            try {
                await this.$api.cancelManualInvoice.put(item.invoiceId)

                item.paymentStatus = 'CANCELED'

                // eslint-disable-next-line no-empty
            } catch (e) {
            } finally {
                this.cancelInvoiceLoading = false
            }
        }

        async editDueDate(item) {
            this.newDueDate = this.$dateFns.format(item.dueDate)
            this.editDueDateItem = item
        }

        async editPayDate(item) {
            this.newPayDate = item.payDate ? this.$dateFns.format(item.payDate) : null
            this.editPayDateItem = item
        }

        reset() {
            if (this.isAgency) {
                this.invoiceType = 'SUPPLIER'
            }
            this.paymentMethods = []
        }

        getStatusColor(item) {
            switch (item.paymentStatus) {
                case 'BILL':
                    return 'text-error'
                case 'PAID':
                    return 'text-success'
                default:
                    return 'text-grey'
            }
        }

        isShowInfoButton(item) {
            return (
                (item.paymentInfos &&
                    item.paymentInfos.length &&
                    (item.paymentInfos.find(i => !!i.remark) ||
                        item.paymentInfos.find(i => Object.keys({...i.bankTransferDetails}).length) ||
                        item.paymentInfos.find(i => Object.keys({...i.exchangeRatesMap}).length))) ||
                !!item.description
            )
        }

        isShowCancelButton(invoice) {
            if (invoice.paymentStatus === 'PAID') {
                if (this.$conf.public.account.paymentSchedulerPayBackdatingAndPaidInvoiceCancelForDirector) {
                    return (
                        this.isTO1 &&
                        ['director', 'support'].includes(this.personRole) &&
                        invoice.paymentStatus === 'PAID'
                    )
                }

                return false
            }

            return invoice.dueDate && invoice.paymentStatus !== 'CANCELED'
        }

        get isLoading() {
            return (
                this.isManualInvoicesLoading ||
                this.paymentOptionsLoading ||
                this.newDueDateSaveLoading ||
                this.newPayDateSaveLoading ||
                this.cancelInvoiceLoading
            )
        }

        get headers() {
            const headers = [
                {title: this.$t('invoices.due_date'), value: 'dueDate'},
                {title: this.$t('invoices.amount'), value: 'amountToPay', align: 'center'},
                {title: this.$t('status'), value: 'paymentStatus'},
                {title: this.$t('supplier_name'), value: 'supplierName'},
            ]

            if (this.manualInvoices.find(m => !!m.payDate)) {
                headers.push({title: this.$t('invoices.pay_date'), value: 'payDate'})
            }

            if (this.manualInvoices.find(m => m.paymentStatus === 'BILL')) {
                headers.push({title: this.$t('pay'), value: 'pay', align: 'center'})
            }

            if (this.isTO1 && this.manualInvoices.find(m => m.dueDate && m.paymentStatus !== 'CANCELED')) {
                headers.push({title: this.$t('cancel'), value: 'cancel', align: 'center'})
            }

            return headers
        }

        get invoiceTypes() {
            return [
                {title: this.$t('supplier_payments'), value: 'SUPPLIER'},
                {title: this.$t('client_payments'), value: 'CUSTOMER'},
            ]
        }

        get isShowTypeSwitcher() {
            return this.isTO1 || this.isTO2
        }

        get isTO1() {
            return authStore.isTO1
        }

        get isTO2() {
            return authStore.isTO2
        }

        get isAgency() {
            return authStore.isAgency
        }

        get isManualInvoicesLoading() {
            return this._isManualInvoicesLoading
        }

        set isManualInvoicesLoading(val) {
            this.changeIsManualInvoicesLoading(val)
        }

        get manualInvoicesFiltered() {
            return this.isShowCancelledPayments
                ? this.manualInvoices
                : this.manualInvoices.filter(i => i.paymentStatus !== 'CANCELED')
        }

        get personRole() {
            return authStore.personRole
        }

        get invoiceBackdatingDays() {
            return (
                +runtimeStore.getCRMOption('asp.option.backdating.and.cancel.paid.manual.invoices.for.director.days') ||
                0
            )
        }

        get minPayDate() {
            if (
                this.$conf.public.account.paymentSchedulerPayBackdatingAndPaidInvoiceCancelForDirector &&
                this.isTO1 &&
                ['director', 'support'].includes(this.personRole)
            ) {
                return null
            } else if (
                this.$conf.public.account.paymentSchedulerPayBackdatingAndPaidInvoiceCancelForDirector &&
                ['supervisor'].includes(this.personRole) &&
                this.invoiceBackdatingDays > 0
            ) {
                return this.$dateFns.format(this.$dateFns.subDays(new Date(), this.invoiceBackdatingDays))
            }

            return this.$dateFns.format(new Date())
        }
    }

    export default OrderPaymentSchedule
</script>
